<script setup lang="ts">
/**
 * A button that puts one exact piece of text on the clipboard, such as a code a person has to paste somewhere else.
 *
 * It says what happened in words: "Copied" once the browser took the text, and how to copy it by hand when the
 * browser refused (the clipboard needs a secure page and the person's permission, and may have neither). The text
 * itself stays on screen beside the button, so a refusal is never a dead end.
 *
 * "Copied" is about one press of one value: it goes back to the button's own words after a moment, so pressing again
 * reads as a fresh copy, and at once when the value changes, so the button never claims a copy of text it never put on
 * the clipboard.
 */
import { Check, Copy } from '@lucide/vue'
import { onBeforeUnmount, ref, watch } from 'vue'
import { Button } from './ui/button'

const props = defineProps<{
    /** The exact text the button puts on the clipboard. */
    value: string
    /** What the button says before it is pressed. "Copy" when absent. */
    label?: string
}>()

/** How long "Copied" stays before the button says its own words again. */
const COPIED_FOR_MS = 2000

/** Where the last press stands: not pressed yet, copied, or refused by the browser. */
const state = ref<'idle' | 'copied' | 'refused'>('idle')
let backToIdle: ReturnType<typeof setTimeout> | null = null

function stopWaiting(): void {
    if (backToIdle) {
        clearTimeout(backToIdle)
        backToIdle = null
    }
}

watch(
    () => props.value,
    () => {
        stopWaiting()
        state.value = 'idle'
    },
)

onBeforeUnmount(stopWaiting)

async function copy(): Promise<void> {
    const copying = props.value
    stopWaiting()
    try {
        if (!navigator.clipboard) {
            throw new Error('clipboard unavailable')
        }
        await navigator.clipboard.writeText(copying)
        // The value changed while the browser was writing: what it holds is not what this button now shows.
        if (copying !== props.value) {
            return
        }
        state.value = 'copied'
        backToIdle = setTimeout(() => {
            backToIdle = null
            state.value = 'idle'
        }, COPIED_FOR_MS)
    } catch {
        if (copying === props.value) {
            state.value = 'refused'
        }
    }
}
</script>

<template>
    <span class="inline-flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" @click="copy">
            <Check v-if="state === 'copied'" aria-hidden="true" />
            <Copy v-else aria-hidden="true" />
            {{ state === 'copied' ? 'Copied' : (label ?? 'Copy') }}
        </Button>
        <span v-if="state === 'refused'" role="alert" class="text-muted-foreground text-sm">
            Couldn't copy. Select the code and copy it yourself.
        </span>
    </span>
</template>
